<template>
	<div class="rs-suggwrap">
		<div class="rs-field">
			<div class="inner">
				<span class="pico" v-html="shareIcons.person"></span>
				<input
					ref="inputRef"
					v-model="query"
					:aria-invalid="!!emailError || searchFieldProps(query)['aria-invalid']"
					v-test="'project-share-dialog-invite-input'"
					type="text"
					placeholder="Add by name, email, or phone number…"
					autocomplete="off"
					@focus="open = true"
					@blur="onBlur"
					@keyup.enter="onEnter" />
			</div>
			<span class="trail">
				<span
					class="rs-plusbtn"
					v-test="'project-share-dialog-create-contact'"
					style="cursor: pointer; display: flex"
					title="Create a contact"
					@mousedown.prevent
					@click="onCreateContact"
					v-html="shareIcons.plus"></span>
			</span>
			<div
				ref="suggestionsRef"
				class="rs-sugg"
				:class="{ open: showDropdown }"
				v-test="'project-share-dialog-suggestions'"
				@mousedown.prevent="onSuggMouseDown">
				<template v-if="teamCard">
					<div class="tm-card">
						<span class="tm-ico" v-html="teamCard.variant === 'company' ? shareIcons.team : shareIcons.check2"></span>
						<div class="tm-body">
							<strong>{{ teamCard.title }}</strong>
							<span>{{ teamCard.body }}</span>
							<div v-if="teamCard.variant === 'member' || teamCard.variant === 'company'" class="tm-actions">
								<button
									class="tm-primary"
									type="button"
									:data-tmshare="teamCard.email"
									:data-member="teamCard.variant === 'member' ? '1' : '0'">
									{{ teamCard.variant === "member" ? "Add to project" : "Add as editor" }}
								</button>
								<button
									v-if="teamCard.variant === 'company' && canManageTeam"
									class="tm-secondary"
									type="button"
									:data-tmteam="teamCard.email">
									Add to team instead
								</button>
							</div>
						</div>
					</div>
				</template>
				<template v-else>
					<!-- AI-review finding 2 (231f07b71c): the failed-search message used to live ONLY in the
				     zero-hit empty state, so a stale row that happened to match the new query hid the
				     failure entirely and the un-searched slice read as a successful search. Warn ABOVE
				     the results instead of hiding them — hiding a contact the user can see is a worse
				     outcome than showing it with a caveat. Distinct class so `.rs-empty` selectors keep
				     resolving to the empty state. -->
					<!-- Round-5 finding 2 (ls-heroku-ci HIGH): teamMembers/customers only switch to the searched
				     sets once directoryQueryActive flips on a SUCCESSFUL settle, while directorySearching
				     flips synchronously on the keystroke — so for the debounce + round-trip window these
				     rows come from the previous/suggested slice. They are NOT hidden: they match what was
				     typed, and blanking the list on every keystroke would both flash and hide a real
				     contact (and would regress the below-minimum "keep typing" state, which shows
				     suggested matches with no search running at all). They just stop claiming to be a
				     settled result. Gated on `searching`, which is false below the minimum. -->
					<div v-if="searching && !queryBelowSearchMinimum && !alreadyAdded && directoryHits.length" class="rs-srchpending">
						Still searching your team and contacts &mdash; these results may not be complete yet.
					</div>
					<!-- The failure caveat is about a LIST, so it needs one: with zero hits the .rs-empty
				     failure copy below already says it, and both rendering was two messages for one
				     failure (ls-heroku-ci medium/low). -->
					<div
						v-if="
							searchFailed && !searching && !queryBelowSearchMinimum && !alreadyAdded && (directoryHits.length || inviteRow)
						"
						class="rs-srchwarn">
						We couldn't finish searching your team and contacts, so this list may be incomplete. Check your connection and try
						again.
					</div>
					<!-- Merge of develop (LSP-18863): an already-shared row stays VISIBLE and is rendered
				     disabled rather than filtered out, so a search can explain that the person already has
				     access instead of reporting no match. `alreadyHasAccess` is decided in directoryHits
				     from the identifier the query MATCHED, not just the primary — see the note there. -->
					<div
						v-for="hit in directoryHits"
						:key="hit.id"
						class="rs-srow"
						:class="{ disabled: hit.alreadyHasAccess }"
						:data-sg="hit.id"
						:data-already-access="hit.alreadyHasAccess ? '1' : undefined"
						:aria-disabled="hit.alreadyHasAccess ? 'true' : undefined">
						<div class="r-cava">{{ initials(hit.name) }}</div>
						<div class="sm">
							<div class="sn">{{ hit.name }}</div>
							<div class="se" :title="hit.subtitle">{{ hit.subtitle }}</div>
						</div>
						<span class="sadd">{{ hit.alreadyHasAccess ? "Already has access" : "Add" }}</span>
					</div>
					<div v-if="inviteRow" class="rs-srow invite" :data-invite="inviteRow.value" :data-phone="inviteRow.phone ? '1' : '0'">
						<span class="plus" v-html="shareIcons.plusS"></span>
						<div class="sm">
							<div class="sn">Invite {{ inviteRow.value }}</div>
							<div class="se">
								{{
									inviteRow.phone ? "They'll get a text — the link is tied to this number" : "They'll get an email invite"
								}}
							</div>
						</div>
					</div>
					<div v-if="alreadyAdded" class="rs-empty">
						<strong>{{ alreadyAdded }}</strong> already has access to this project &mdash; manage or resend their invite in the
						list below.
					</div>
					<div v-else-if="!directoryHits.length && !inviteRow" class="rs-empty">
						<!-- PR #3838 review H1: a query under the parent's search minimum never reaches the
					     server, so "no people match" would be asserting a negative nothing checked (a
					     1-char query used to filter the FULLY loaded roster and could surface matches).
					     PR #3838 review H2: a FAILED search never updated the slice either, and this is
					     the ONLY state a free-text name query renders — teamCard bows out for
					     non-email/non-phone queries, so searchFailed had no consumer on that path. -->
						<template v-if="queryBelowSearchMinimum">Keep typing to search your team and contacts</template>
						<template v-else-if="searching">Searching…</template>
						<template v-else-if="searchFailed">
							We couldn't search your team and contacts. Check your connection and try again.
						</template>
						<template v-else>No people match "{{ query.trim() }}"</template>
					</div>
				</template>
				<!-- The open-menu copy stays inside the height cap; the hidden copy below reserves the dialog's layout. -->
				<p class="rs-caption">
					Search teammates and existing contacts — or tap <b>+</b> to create a new <b>contact</b> (someone who isn't on your
					team).
				</p>
			</div>
		</div>
		<p v-if="emailError" role="alert" class="text-caption">{{ emailError }}</p>
		<p v-if="searchFieldProps(query).counter" class="text-caption text-error">
			{{ searchFieldProps(query).counterValue() }}/{{ searchFieldProps(query).counter }}
			{{ searchFieldProps(query).errorMessages[0] }}
		</p>
		<p :class="{ 'rs-caption-under-menu': showDropdown }" :aria-hidden="showDropdown ? 'true' : undefined" class="rs-caption">
			Search teammates and existing contacts — or tap <b>+</b> to create a new <b>contact</b> (someone who isn't on your team).
		</p>
	</div>
</template>

<script setup lang="ts">
	import { VuetifyValidation, FIELD_LENGTHS } from "@/helpers/Validation";
	import { isSearchTooLong, searchFieldProps } from "@/helpers/searchLimit";
	import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
	import type { CountryCode } from "libphonenumber-js";
	import { shareIcons } from "./shareIcons";
	import { toE164 } from "@/helpers/phone";
	import { logError } from "@/helpers/logError";
	import { isPublicEmailDomain } from "@/helpers/publicEmailDomains";
	import { emailComparisonKey } from "@/helpers/projectAccess";
	import {
		MIN_DIRECTORY_SEARCH_LENGTH,
		MIN_PHONE_DIGITS,
		isEmailQuery,
		isPhoneQuery,
		phoneMatchesCandidates,
		phoneSearchCandidates
	} from "@/helpers/directoryQuery";

	interface DirectoryItem {
		id: string;
		name: string;
		email?: string;
		phone?: string;
		// Bugbot MEDIUM (thread 3861233836): EVERY address/number the server could have matched on.
		// api.searchCustomers filters the ASSOCIATION columns ($EmailAddresses.emailAddress$,
		// $PhoneNumbers.phonenumberraw$), so a customer is returned when ANY of theirs matches — and a
		// filter that re-tested only the scalar HID rows the server had returned. Optional, and the
		// helpers below fall back to the scalar, so a caller (or an isolated mount) that supplies only
		// `email`/`phone` keeps its exact previous behavior.
		emails?: string[];
		phones?: string[];
	}

	// Where the invite for a directory row is SENT. Bugbot thread 3865503645: this is deliberately a
	// separate value from anything the row DISPLAYS, because the two answer different questions and
	// sharing one field between them is the recurring defect on this file.
	//
	//   display  — "what address should I show as context?"  A primary-address fallback is CORRECT.
	//   staging  — "which identifier did the query match?"   A fallback is WRONG: absence is the
	//                                                        answer, and absence is what makes the
	//                                                        phone branch reachable.
	//
	// `via: "matched"` means the query matched this exact value. `via: "primary"` is the explicit
	// NAME-query decision (see `stageTargetFor`) — not a fallback that leaked in from display.
	interface StageTarget {
		field: "email" | "phone";
		value: string;
		via: "matched" | "primary";
	}

	/**
	 * HOW a query came to match one of a row's identifiers, and therefore how much authority that
	 * identifier has over where the invite is sent.
	 *
	 * Bugbot HIGH at b9d1a9cecd. The tier used to be computed inside `directoryHits` and then thrown
	 * away — the matcher returned bare strings and `stageTargetFor` applied an email-before-phone rule
	 * that could not see it. That is the same "two places encoding one rule" defect the tier was
	 * introduced to fix, so the tier now TRAVELS with the identifier and is compared FIRST.
	 *
	 *   explicit   — the user typed this identifier: an `@`-bearing word matching this address, or a
	 *                number matching the query's digits.
	 *   incidental — a plain word merely happens to be a substring of it (a name fragment inside an
	 *                address). Never a phone: see `attributablePhoneFor`.
	 */
	type MatchTier = "explicit" | "incidental";

	interface MatchedIdentifier {
		value: string;
		tier: MatchTier;
	}

	/** What the single evaluation in `directoryHits` reports. `undefined` = nothing of that kind matched. */
	interface IdentifierMatch {
		email?: MatchedIdentifier;
		phone?: MatchedIdentifier;
	}

	// The shape `directoryHits` produces. Named so the ONE staging producer below can take it, and
	// so `matchedEmail`/`matchedPhone`/`stageTarget` are part of a documented contract rather than an
	// inferred detail that a future edit could drop without any call site complaining.
	interface DirectoryHit {
		id: string;
		name: string;
		email?: string;
		phone?: string;
		emails: string[];
		phones: string[];
		// STRICTLY what the query matched — `undefined` means "the query matched nothing email-shaped
		// / phone-shaped on this row". Neither carries a primary fallback: `matchedEmail` used to
		// (`|| item.email`), which silently emailed a contact found BY PHONE and dropped the
		// `customerId` that only the phone branch carries (Bugbot thread 3865503645). The subtitle
		// applies its own fallback locally, where that intent is documented.
		matchedEmail?: string;
		matchedPhone?: string;
		// The single resolved answer to "where does Add send this invite?", decided once in
		// `directoryHits` where the match knowledge lives. `null` = nothing stageable on this row.
		stageTarget: StageTarget | null;
		subtitle: string;
		// develop (LSP-18863): the row is rendered but non-actionable. Part of the contract so the
		// click and Enter guards cannot be added to one path and forgotten on the other.
		alreadyHasAccess: boolean;
	}

	interface StagedEntry {
		email?: string;
		phone?: string;
	}

	const props = defineProps<{
		directory: DirectoryItem[];
		staged: StagedEntry[];
		usedIdentifiers: string[];
		teamEmails: string[];
		myDomain: string;
		// Org default country (ISO, e.g. "US" / "IN") used to normalize a typed national-format
		// phone into E.164 before dedup — mirrors the parent's usedIdentifiers, which stores each
		// existing share phone as toE164(...). Defaults to "US" (toE164's own default country).
		defaultCountry?: string;
		// LSP-15692: true while the parent's server-side directory search is in flight —
		// renders "Searching…" instead of a premature "No people match" empty state.
		searching?: boolean;
		// Bugbot follow-up (PR #3838): true when the current query's search FAILED (never set by
		// aborted/superseded requests). The slice was never updated, so the typed email stays in
		// the unresolved (action-less) state instead of falling through to company/invite.
		searchFailed?: boolean;
		// PR #3838 review H1: the parent's MIN_DIRECTORY_SEARCH_LENGTH — the number of characters
		// below which no server search happens at all. The empty state needs it so it can ask for
		// more input instead of claiming nothing matched. Defaults to the parent's own value so an
		// isolated mount behaves like the wired one.
		minSearchLength?: number;
		// LSP-17773 (review): whether the caller can reach team management (/users is
		// admin-gated). Gates the "Add to team instead" action — a plain User clicking it
		// would only get bounced to home by the router guard.
		canManageTeam?: boolean;
	}>();

	const emit = defineEmits<{
		stage: [entry: Record<string, unknown>];
		"create-contact": [];
		// LSP-15692: every keystroke of the invite query — the parent debounces this into a
		// server-side directory search (team members + contacts) instead of pre-loading everything.
		"query-change": [query: string];
		"add-to-team": [email: string];
	}>();

	const inputRef = ref<HTMLInputElement | null>(null);
	const suggestionsRef = ref<HTMLElement | null>(null);
	const query = ref("");
	const open = ref(false);

	const emailError = ref("");
	const emailRule = VuetifyValidation.EmailValidation(
		"Please enter a valid email address.",
		`Email address must not exceed ${FIELD_LENGTHS.EMAIL} characters.`
	);

	watch(query, (value) => {
		emailError.value = "";
		emit("query-change", value);
	});

	function validateEmailQuery(action: "enter" | "create-contact"): boolean {
		const value = query.value.trim();
		// Names and multi-term directory searches (including a name plus an email) are not email entries.
		const result = value.includes("@") && !/\s/.test(value) ? emailRule.isValid(value) : true;
		// The search counter already explains overflow; do not show a competing email limit.
		emailError.value = typeof result === "string" && !isSearchTooLong(query.value) ? result : "";
		if (typeof result === "string") {
			console.warn("ShareInviteField: email action refused", { action, reason: "invalid_email" });
			return false;
		}
		return true;
	}

	function onCreateContact() {
		if (validateEmailQuery("create-contact")) emit("create-contact");
	}

	// PR #3838 (Bugbot HIGH): the shape rule now lives in @/helpers/directoryQuery so this filter and
	// BOTH server-side predicates (ShareActionDialog.buildUserSearchWhere, api.searchCustomers) agree
	// on what counts as a phone. They previously disagreed, which is how a pasted number could be
	// dropped by one of them.
	//
	// Merge of develop (duplicate-fix collision): develop replaced this component's local
	// `VuetifyValidation.EmailValidation` rule while this PR was in flight. Both intents survive —
	// `isEmailQuery` is still the single shape test shared with the server-side predicates, and it
	// now delegates the EMAIL half to `VuetifyValidation.EmailValidation` rather than carrying its own
	// regex, so this component, ShareActionDialog and the validator can no longer disagree.
	function isEmail(value: string) {
		return isEmailQuery(value);
	}
	function isPhone(value: string) {
		return isPhoneQuery(value);
	}
	// The full match sets, scalar-inclusive. The scalar is folded in rather than assumed to be
	// arrays[0] so that a caller passing ONLY the scalar still matches on it, and a caller passing
	// both cannot lose the primary value if the arrays are ever built from a different source.
	function itemEmails(item: DirectoryItem): string[] {
		return [...new Set([item.email, ...(item.emails ?? [])].filter((value): value is string => !!value))];
	}
	function itemPhones(item: DirectoryItem): string[] {
		return [...new Set([item.phone, ...(item.phones ?? [])].filter((value): value is string => !!value))];
	}
	function initials(name: string) {
		const parts = (name || "").trim().split(/\s+/).filter(Boolean);
		if (!parts.length) return "?";
		if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
		return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
	}

	// Normalize a phone into lower-cased E.164 using the org default country, so a typed
	// national-format number ("8650442828") matches an existing share stored as E.164
	// ("+918650442828"). Returns null when the value can't be assembled into E.164 yet.
	function phoneKey(value: string): string | null {
		const e164 = toE164(value, (props.defaultCountry || "US") as CountryCode);
		return e164 ? e164.toLowerCase() : null;
	}

	// Identity-level access: the contact's directory id (a CUSTOMER/ACCOUNT-type share is keyed on it),
	// or — for a teammate — the account email, which is what Add grants for a teammate whichever
	// identifier the query matched. Deliberately NOT the contact's primary email/phone for everyone:
	// the roster's `usedIdentifiers` also pools identifiers that belong to OTHER rows (the sharer's own
	// profile phone, the creator's email), so a contact whose primary number merely equals one of those
	// was disabled as "Already has access" with no share of their own (LSP-22129). A non-teammate's
	// disabled state is decided by the identifier Add would actually send to, in
	// `stagedIdentifiersHaveAccess`.
	function itemAlreadyHasAccess(item: DirectoryItem): boolean {
		if (usedSet.value.has(item.id.toLowerCase())) return true;
		const isTeammate = !!item.email && teamSet.value.has(emailComparisonKey(item.email));
		return isTeammate && usedSet.value.has(emailComparisonKey(item.email!));
	}

	/**
	 * WHERE Add sends this row's invite. The ONE place that decides, so the answer cannot be
	 * re-derived (and drift) at the staging site — Bugbot thread 3865503645.
	 *
	 * Precedence, and why:
	 *
	 *  1. The MORE EXPLICIT match wins. An identifier the user actually typed always outranks one that
	 *     a name fragment merely happened to be a substring of, whichever kind each is. This is the
	 *     Bugbot HIGH at b9d1a9cecd: "Dana 4150001111" attributes the address incidentally ("Dana" is
	 *     inside it) and the number explicitly, and the old unconditional email-before-phone rule
	 *     emailed the contact anyway — dropping the `customerId` that only the phone branch carries.
	 *  2. WITHIN one tier, email before phone. The address is the more specific signal, and email is
	 *     the reversible channel (a mistaken text cannot be unsent). This is the rule that used to be
	 *     applied unconditionally; it is correct only as a tie-break, because comparing an explicit
	 *     match against an incidental one is not a tie.
	 *  3. Otherwise the query matched NO identifier at all — a plain NAME query, or digits that named
	 *     no number in full. Send to the contact's canonical PRIMARY, address before number: a name
	 *     query expresses no preference between a person's identifiers, so the primary is the only
	 *     non-arbitrary choice. `via: "primary"` records that this was chosen, not matched.
	 *  4. Nothing stageable — no matched identifier and no primary of either kind.
	 *
	 * What this deliberately does NOT do is fall back from a matched PHONE to a primary EMAIL. That
	 * was the original defect: `matchedEmail` carried `|| item.email`, so a contact found by their
	 * number was emailed instead of texted and lost the `customerId`.
	 */
	function stageTargetFor(item: DirectoryItem, match: IdentifierMatch | null): StageTarget | null {
		const email = match?.email;
		const phone = match?.phone;
		if (email && phone && email.tier !== phone.tier) {
			// Cross-tier: explicit wins outright. Email-before-phone must not be consulted here.
			return email.tier === "explicit"
				? { field: "email", value: email.value, via: "matched" }
				: { field: "phone", value: phone.value, via: "matched" };
		}
		if (email) return { field: "email", value: email.value, via: "matched" };
		if (phone) return { field: "phone", value: phone.value, via: "matched" };
		if (item.email) return { field: "email", value: item.email, via: "primary" };
		if (item.phone) return { field: "phone", value: item.phone, via: "primary" };
		return null;
	}

	// develop's LSP-18863 predicate above answers "does this PERSON already have access" from the id
	// (and a teammate's account email; LSP-22129 dropped the primary email/phone fallback for everyone
	// else). This PR stages the identifier the query MATCHED, which may be a
	// SECONDARY address or number (Bugbot threads 3861233836 / 3861458841) — so the primary-only
	// question is not the one that keeps Add from minting a duplicate share. Ask it about the value
	// stageDirectoryHit would actually send to instead: the row is informational exactly when the
	// share it would create already exists. Taking the resolved StageTarget rather than a pair of
	// loose strings is what keeps that "exactly when" true by construction — the disabled state and
	// the staged identifier can no longer be computed from different inputs.
	function stagedIdentifiersHaveAccess(item: DirectoryItem, target: StageTarget | null): boolean {
		if (itemAlreadyHasAccess(item)) return true;
		if (!target) return false;
		if (target.field === "email") return usedSet.value.has(emailComparisonKey(target.value));
		// Both indexes, as the old filter did: usedSet carries staged phones in their RAW lower-cased
		// form as well as normalized E.164, and a number that cannot be assembled into E.164 yet is
		// still matchable on its bare digits.
		const stagedDigits = target.value.replace(/\D/g, "").toLowerCase();
		if (stagedDigits && usedSet.value.has(stagedDigits)) return true;
		const normalized = phoneKey(target.value);
		return !!normalized && usedSet.value.has(normalized);
	}

	const usedSet = computed(() => {
		const set = new Set<string>();
		props.usedIdentifiers.forEach((identifier) =>
			set.add(identifier.includes("@") ? emailComparisonKey(identifier) : identifier.toLowerCase())
		);
		props.staged.forEach((entry) => {
			if (entry.email) set.add(emailComparisonKey(entry.email));
			if (entry.phone) {
				set.add(entry.phone.toLowerCase());
				// Also index the E.164 form so a staged national number is matched regardless of
				// which format the next typed value takes.
				const key = phoneKey(entry.phone);
				if (key) set.add(key);
			}
		});
		return set;
	});

	const teamSet = computed(() => new Set(props.teamEmails.map((email) => emailComparisonKey(email))));

	// Same-company email typed in full → teammate-detection card (matches the prototype's tm-card).
	const teamCard = computed(() => {
		const raw = query.value.trim().toLowerCase();
		if (!raw) return null;
		// LSP-15692 (Bugbot): a PHONE query participates in the server-side directory search
		// exactly like an email (searchCustomers $ilike on phonenumberraw, getUsers $ilike on
		// phone, and directoryHits' >=3-digit matcher), so it needs the SAME unresolved gate —
		// otherwise the raw "Invite <number>" row is offered as a resolved choice while the
		// lookup is in flight or after it failed, staging a known contact as a raw phone invite.
		// Free-text queries keep the plain hit-list UX (they can't be staged as a raw invite).
		// Named queryIsEmail/queryIsPhone rather than isEmailQuery/isPhoneQuery: those two names are
		// IMPORTED from @/helpers/directoryQuery, and the old locals shadowed them — so a future edit
		// in this computed calling isPhoneQuery(...) would have hit a boolean, not the function.
		const queryIsEmail = isEmail(raw);
		const queryIsPhone = !queryIsEmail && isPhone(raw);
		if (!queryIsEmail && !queryIsPhone) return null;
		const domain = queryIsEmail ? raw.split("@")[1] || "" : "";
		if (queryIsPhone) {
			// An already-shared number is reported by the alreadyAdded row (which normalizes to
			// E.164 like usedSet does) — leave that path alone.
			const phoneUsedKey = phoneKey(raw);
			if (usedSet.value.has(raw) || (phoneUsedKey && usedSet.value.has(phoneUsedKey))) return null;
		}
		// NOTE (review rounds 1–2): "Already added" and "is on your team" are EXACT-identity
		// matches, valid on ANY domain — a teammate whose email lives on a different domain than
		// the sharer's (or on a free-mail domain) must still be recognized. Only the
		// same-company INFERENCE further down is domain-based, so only it is gated on the
		// sharer's domain and suppressed for public providers.
		if (queryIsEmail && usedSet.value.has(emailComparisonKey(raw))) {
			return {
				variant: "added" as const,
				email: raw,
				title: "Already added",
				// Same guidance as the rs-empty alreadyAdded row (review): this card intercepts
				// full EMAILS before that row can render, and losing the "manage or resend" pointer
				// left the user with a dead end.
				body: `${raw} already has access to this project — manage or resend their invite in the list below.`
			};
		}
		if (queryIsEmail && teamSet.value.has(emailComparisonKey(raw))) {
			return {
				variant: "member" as const,
				email: raw,
				title: `${raw} is on your team`,
				body: "Add them and the project shows up right away — they're already signed in, nothing more to do."
			};
		}
		// LSP-15692 (Bugbot): teamEmails is now only the suggested/searched SLICE of the roster,
		// so absence from teamSet is NOT evidence of non-membership while the server-side
		// directory search for this query is still resolving (the parent flips `searching` on
		// synchronously with the keystroke). Until it resolves, make NO membership claim rather than
		// misclassifying an existing teammate as "not on your team yet" — the search's $ilike on email
		// is the authoritative lookup: a typed teammate email lands in teamSet when it returns, and
		// absence after resolution is a real non-member.
		// Round 7 (ls-heroku-ci FULL review at 916f006b68): this returned an action-less `checking`
		// card, and that remedy had exactly the dead-end shape the `searchFailed` block below already
		// had to unwind. Because `v-if="teamCard"` is mutually exclusive with the `v-else` region that
		// renders directoryHits AND inviteRow, an in-flight identifier-shaped query REPLACED every
		// already-visible directory row with the card — so a contact the user could see, and could
		// have clicked, vanished on the next keystroke and Enter had nothing to act on.
		//
		// What is NOT given up is the reviewed decision the `checking` variant existed for: teamEmails
		// is only the searched SLICE, so slice-absence is not evidence of non-membership while the
		// search is resolving. Returning null preserves that precisely, because the `added` and
		// `member` checks above already ran — POSITIVE evidence still wins — and null skips the one
		// inference that reads slice ABSENCE, the same-company "not on your team yet" card below.
		// The in-flight state is still LABELLED, by `.rs-srchpending` in the v-else region, and the
		// unresolved query is still un-stageable: `inviteRow` suppresses the raw "Invite <address>"
		// row while `searching`, which is the ONE gate both the click and Enter paths read (a guard on
		// only one of that pair is how this file has manufactured contradictions before). Directory
		// hits, whose identities are resolved facts rather than a classification, stay clickable.
		if (props.searching) {
			return null;
		}
		// Round 6 HIGH (ls-heroku-ci): a FAILED search must NOT return a card here.
		//
		// This used to return an action-less `checking` card, on the reasoning that slice-absence
		// proves nothing after a failure. The reasoning was right; the remedy was a functional dead
		// end. The template's `v-if="teamCard"` branch is mutually exclusive with the `v-else` that
		// renders directoryHits AND inviteRow, and `tm-actions` only shows for member/company — so on a
		// search-endpoint outage a user typing an address had NO way to invite anyone at all, while
		// every keystroke re-fired the failing search. Before this PR the same outage left the invite
		// row working. We traded misleading copy for "cannot share the project", which is the worse
		// failure: a cosmetic wrong is recoverable, a blocked flow is not.
		//
		// Returning null hands the query to the `v-else` region, which renders the hits, the invite row
		// and the `.rs-srchwarn` caveat together — the same "warn above, don't replace" shape already
		// chosen for stale hits. Note what this does NOT give up: the `added` and `member` checks above
		// run FIRST, so POSITIVE evidence still wins (an address present in the slice is still a real
		// match even if the latest search failed). Only slice ABSENCE is unreliable, and returning null
		// skips exactly the inference that relies on it — the same-company "not on your team yet" card
		// below. So the user gets a plain, correctly-unclassified invite plus a visible caveat.
		//
		// The in-flight (`searching`) block above is deliberately kept: it clears in one round trip.
		if (props.searchFailed) {
			return null;
		}
		// The same-company INFERENCE below is the only domain-based part of this card: it needs
		// the typed domain to equal the sharer's own, and a shared FREE-MAIL domain proves
		// nothing (an org signing in with gmail.com must not see this card for every gmail
		// address on earth). Non-matching or public domains fall through to the normal invite row.
		// Resolved phone query: absence from the results legitimately means "new invite", so fall
		// through to directoryHits / inviteRow exactly as before. Everything below is email-only.
		if (!queryIsEmail) return null;
		if (!props.myDomain || domain !== props.myDomain.toLowerCase()) return null;
		if (isPublicEmailDomain(domain)) return null;
		// LSP-17773 (review): tell the sharer what each choice actually does — sharing keeps
		// them outside the team; adding to the team creates a new user. No count is quoted
		// (review medium #3): teamEmails is EVERY roster row (disabled and View Only included),
		// not the billable count the seat gate actually charges against, so "N to N+1" implied
		// a billable change that may be wrong — and a 0-length roster (fetch failed) is not
		// knowledge at all. The seat/upgrade checks run in the Add User hand-off, so say that.
		const seatNote = !props.canManageTeam
			? ""
			: " Adding them to your team instead creates a new user — seat availability is checked on the next step.";
		return {
			variant: "company" as const,
			email: raw,
			title: "Same company — but not on your team yet",
			body: `${raw} isn't on your team yet, so they'll be added as an editor.${seatNote}`
		};
	});

	const directoryHits = computed(() => {
		const raw = query.value.trim().toLowerCase();
		if (!raw) return [];
		// LSP-15692 (Bugbot): match per word, mirroring the server's word-split $and-of-$or
		// search — a whole-query substring test dropped server hits like "jo smith" →
		// "Jonathan Smith" (each word matches a different part of the name) or cross-field
		// matches ("jo x.com" → name + email) before they could render.
		const words = raw.split(/\s+/).filter(Boolean);
		// Computed once per query, not per row.
		// "US" (the helper default) to match the other two search sites exactly — see the note in
		// ShareActionDialog.buildUserSearchWhere. phoneKey below still uses the ORG country for dedup.
		const phoneCandidates = phoneSearchCandidates(raw);
		// MERGE NOTE (develop LSP-18863 vs this PR's Bugbot threads 3861233836 / 3861458841).
		//
		// This filter used to `return false` for a row whose MATCHED identifier already had access, so
		// the row never rendered. develop then landed the opposite product decision: keep the row and
		// render it disabled ("Already has access"), because silently hiding it made a real search
		// report "no match" for someone the admin was specifically checking for — it also DELETED the
		// parent-side exclusion in ShareActionDialog.memberItems for the same reason. develop's decision
		// is the newer, shipped one and it wins: hiding here would make develop's disabled row, its
		// aria-disabled, its data-already-access and its cap-fairness split all unreachable dead code.
		//
		// What does NOT get dropped is WHICH identifier the question is asked about. develop's
		// itemAlreadyHasAccess only knows the id and the PRIMARY email/phone, while staging sends to the
		// matched identifier — so keeping develop's rendering with only develop's predicate would offer
		// an actionable "Add" for a contact already shared under their SECOND number and mint a
		// DUPLICATE share, which is exactly the defect thread 3861233836 was filed for. The dedup
		// knowledge therefore moves from the filter into `alreadyHasAccess` in the map below: this PR's
		// broader matching still DECIDES, develop's disabled row is what the user SEES.
		// WHICH of a row's numbers did the query name? Decided from the query's digits as ONE run,
		// never from an individual word.
		//
		// Bugbot HIGH at b9d1a9cecd, second half. Attributing per word let "415 000 1111" — three words,
		// each clearing any per-word digit floor — name a number that shared only the AREA CODE with what
		// was typed, because each fragment matched a different row number and the declared-order
		// tie-break then picked the primary. A digit floor cannot fix that: the fragments are individually
		// long enough and still collectively wrong.
		//
		// So the digits are re-joined and evaluated as one candidate before attribution. That is the only
		// reading that corresponds to user intent — whitespace inside a phone number is formatting, not
		// word structure, which is exactly why `phoneSearchCandidates` exists — and it is also what the
		// pre-LSP-15692 client did (`raw.replace(/\D/g, "")` then `phoneDigits.includes(digits)`), so the
		// subtitle's behaviour for spaced and unspaced numbers is unchanged.
		//
		// Candidates are consulted FIRST because the raw run is wrong for an extension query
		// ("212-555-0100 x123" yields "2125550100123", which no row holds — break-it round 2). If neither
		// names a number, NOTHING phone-shaped is attributable and `stageTargetFor` falls to its
		// documented primary decision: a number nobody typed in full must never be invited by inference.
		const queryDigits = raw.replace(/\D/g, "");
		const attributablePhoneFor = (phones: string[]) => {
			if (phoneCandidates) {
				const candidateHit = phones.find((value) => phoneMatchesCandidates(value, phoneCandidates));
				if (candidateHit) return candidateHit;
			}
			// MIN_PHONE_DIGITS is the SHARED definition of "enough digits to name a number" — the same
			// one `isPhoneQuery` and `phoneSearchCandidates` use. Bugbot MEDIUM at 1bb632e821: this used
			// to be a local `MIN_ATTRIBUTABLE_PHONE_DIGITS = 3`, which granted the EXPLICIT tier to a run
			// this component does not even regard as a phone number, so "Dana 415" outranked a real
			// address match and texted an area-code sibling. Importing the definition rather than
			// restating it is the point: the two cannot disagree on the edge any more.
			if (queryDigits.length < MIN_PHONE_DIGITS) return undefined;
			return phones.find((value) => value.replace(/\D/g, "").includes(queryDigits));
		};
		// ONE evaluation, TWO answers: "does this row match?" and "which of the row's identifiers made
		// it match?" (`null` = no match). Also ONE decision: the tier that evaluation assigns is what
		// `stageTargetFor` obeys, rather than a separate precedence rule it cannot see.
		//
		// Bugbot thread 3865503645 / ls-heroku-ci FULL review at 916f006b68 (wrong-recipient HIGH) /
		// Bugbot HIGH at b9d1a9cecd (the same defect INSIDE the fix for it — see below).
		//
		// Round 1 of this fix: the two questions used to be answered by two functions — a PER-WORD filter
		// plus a `matchedEmailFor` that tested the WHOLE raw query against each address. They agree only
		// for single-word queries. For "Dana dana@vendor.test" the filter matched the row (name word +
		// secondary-address word) while `matchedEmailFor` returned undefined, so `stageTargetFor` fell
		// past both matched branches to the PRIMARY-email fallback and Add invited an address the user
		// never typed.
		//
		// Round 2 (this pass): collapsing those two functions was necessary but NOT sufficient, because
		// the same shape immediately reappeared one layer down. The matcher computed a TIER and then
		// returned bare strings, throwing the tier away, and `stageTargetFor` applied an unconditional
		// email-before-phone precedence that could not consult it. So "Dana 4150001111" attributed the
		// address INCIDENTALLY (the name fragment "Dana" is a substring of it) and the number EXPLICITLY
		// (the user typed it), and precedence still emailed the contact — dropping the `customerId` that
		// only the phone branch carries. Two places deciding one thing, again. The tier now TRAVELS with
		// the identifier and `stageTargetFor` compares tiers FIRST; email-before-phone survives only as a
		// tie-break WITHIN one tier, so an incidental hit can never outrank an explicit one.
		//
		// DETERMINISM (review trap 1). When a query touches two of a row's identifiers the reported one
		// must not depend on iteration order, so within a tier the ROW's declared order decides —
		// `itemEmails`/`itemPhones` put the primary scalar first.
		//
		//   * EXPLICIT — the user typed the identifier itself: an `@`-bearing word for an address, or a
		//     number matching the query's digits for a phone.
		//   * INCIDENTAL — a plain word that merely happens to be a substring of an address (a name
		//     fragment landing inside it). There is deliberately NO incidental PHONE tier; see
		//     `attributablePhoneFor`.
		const matchIdentifiers = (item: DirectoryItem): IdentifierMatch | null => {
			// Every address/number this row could legitimately be found by (Bugbot thread 3861233836).
			const emails = itemEmails(item);
			const phones = itemPhones(item);
			const name = item.name.toLowerCase();
			const explicitEmails: string[] = [];
			const incidentalEmails: string[] = [];
			// Decided ONCE per row, from the whole query rather than from any single word.
			const attributedPhone = attributablePhoneFor(phones);
			const resolve = (): IdentifierMatch => {
				const email =
					emails.find((value) => explicitEmails.includes(value)) ?? emails.find((value) => incidentalEmails.includes(value));
				return {
					email: email ? { value: email, tier: explicitEmails.includes(email) ? "explicit" : "incidental" } : undefined,
					// Always explicit when present — an incidental phone attribution is not representable.
					phone: attributedPhone ? { value: attributedPhone, tier: "explicit" } : undefined
				};
			};
			if (phoneCandidates) {
				// PR #3838 (Bugbot HIGH / AI-review finding 1): for a PHONE-shaped query, match the
				// whole query's digits as ONE term — restoring the pre-LSP-15692 behavior. Per-word
				// matching broke a pasted "+1 212 555 0100": the "+1" word yields one digit and `every`
				// then rejected a row the server had correctly returned. ADDITIVE, mirroring both server
				// predicates: OR-ed with the per-word test below, so the client never hides a row either
				// server would return.
				const wholeQueryEmails = emails.filter((value) => value.toLowerCase().includes(raw));
				const candidatePhones = phones.filter((value) => phoneMatchesCandidates(value, phoneCandidates));
				if (name.includes(raw) || wholeQueryEmails.length || candidatePhones.length) {
					// A phone-shaped query can never carry "@" (PHONE_RE rejects it), so a whole-query
					// address hit here is a number matching an address's LOCAL PART ("7075550100" vs
					// "7075550100@acme.com") — an INCIDENTAL hit, not a typed address. Recorded because it is
					// still what the row matched on, but it must not outrank the number that matched.
					incidentalEmails.push(...wholeQueryEmails);
					return resolve();
				}
			}
			// Names keep per-word matching so "jo smith" still mirrors the server's $and-of-$or
			// (break-it round 2).
			const everyWordMatched = words.every((word) => {
				const wordDigits = word.replace(/\D/g, "");
				const emailHits = emails.filter((value) => value.toLowerCase().includes(word));
				(word.includes("@") ? explicitEmails : incidentalEmails).push(...emailHits);
				// VISIBILITY mirrors the server predicate exactly, with NO minimum length: both server
				// halves test each word as `%word%` against the contiguous-digit phone column, so the
				// query "jo 55" matches a row whose number contains "55" server-side. The client filter
				// used to require >= 3 digits here and DROPPED that row (ls-heroku-ci FULL review at
				// 916f006b68) — and a row dropped client-side is unrecoverable, because the server
				// result it came from is already discarded. The client filter must never be stricter
				// than the predicate that produced its input.
				//
				// These per-word phone hits decide VISIBILITY ONLY and are deliberately not recorded as
				// attributions — that is the whole point of `attributablePhoneFor`.
				const phoneHits = wordDigits ? phones.filter((value) => value.replace(/\D/g, "").includes(wordDigits)) : [];
				return name.includes(word) || emailHits.length > 0 || phoneHits.length > 0;
			});
			return everyWordMatched ? resolve() : null;
		};
		const matchingItems = props.directory
			.map((item) => ({ item, match: matchIdentifiers(item) }))
			.filter((entry): entry is { item: DirectoryItem; match: IdentifierMatch } => !!entry.match)
			.map(({ item, match }) => {
				// Both come from the SINGLE evaluation above (Bugbot thread 3861233836 / the
				// wrong-recipient HIGH): the number and address shown are the ones that MATCHED, searched
				// across all of the row's identifiers rather than just the primaries. Rendering the
				// primary for a row found by its secondary would answer a question the user did not ask —
				// they typed identifier X and would be shown identifier Y, with nothing to explain it.
				//
				// Bugbot thread 3865503645: `matchedEmail` used to carry `|| item.email`. That fallback was
				// added for the SUBTITLE — "a name query must still show the contact's main address" — and
				// was correct there. Then a later round made this same field the value STAGING reads, and
				// one field was answering two questions: for staging a fallback is not a nicety but a bug,
				// because `stageDirectoryHit` prefers email over phone, so a contact found by their NUMBER
				// was emailed at their primary address and the `customerId` carried only on the phone
				// branch was silently dropped. The fallback now lives on the two lines below, local to the
				// display value, where its intent is documented and where it cannot reach staging.
				const matchedPhone = match.phone?.value;
				const matchedEmail = match.email?.value;
				// DISPLAY only. A name query matched no specific address, but the row still needs the
				// contact's main address as context — exactly as it always did.
				const subtitleEmail = matchedEmail || item.email;
				const subtitle = [subtitleEmail, matchedPhone || ""].filter(Boolean).join(" · ");
				// STAGING only, decided once, here. See stageTargetFor for the precedence and for the
				// explicit name-query decision.
				// The TIERED match, not the bare strings: the tier is what decides, and stripping it here
				// is precisely how the Bugbot HIGH at b9d1a9cecd got in.
				const stageTarget = stageTargetFor(item, match);
				// `emails`/`phones` ride along so inviteRow's suppression below can ask "was a hit rendered
				// FOR this typed value?" using the SAME match sets the filter just used. Widening the
				// filter without widening the suppression would re-open the duplicate-invite bug from
				// round-5 finding 1 in a new place: a contact matched on its secondary number would render
				// AND be shadowed by a redundant raw "Invite <number>" row.
				// `stageTarget` is what Add sends to, and `subtitle` is built from the same matched values,
				// so what the row SHOWS and what the invite is SENT to still agree by construction rather
				// than by coincidence (Bugbot thread 3861458841) — but they are now two values with two
				// documented intents, not one field doing both jobs (Bugbot thread 3865503645).
				// matchedEmail/matchedPhone stay possibly-undefined; that undefined is load-bearing, since
				// it is what tells `stageTargetFor` the query matched nothing of that kind.
				return {
					id: item.id,
					name: item.name,
					email: item.email,
					phone: item.phone,
					emails: itemEmails(item),
					phones: itemPhones(item),
					matchedEmail,
					matchedPhone,
					stageTarget,
					subtitle,
					// develop's flag, this PR's breadth: asked about the very value stageDirectoryHit would
					// send to, so "the row is disabled" and "Add cannot duplicate a share" are the same
					// fact rather than two predicates that can drift.
					alreadyHasAccess: stagedIdentifiersHaveAccess(item, stageTarget)
				};
			});
		const eligibleItems = matchingItems.filter((item) => !item.alreadyHasAccess).slice(0, 6);
		// Already-shared matches are informational and cannot be staged. Keep them visible even
		// when six eligible directory rows match the same broad search, otherwise the user the
		// admin was checking for disappears behind the action-oriented suggestion cap.
		const existingAccessItems = matchingItems.filter((item) => item.alreadyHasAccess).slice(0, 6);
		return [...eligibleItems, ...existingAccessItems];
	});

	const inviteRow = computed(() => {
		const value = query.value.trim();
		if (!value) return null;
		const email = isEmail(value);
		const phone = isPhone(value);
		if (!email && !phone) return null;
		// Round 7 (ls-heroku-ci FULL review at 916f006b68): the ONE gate that keeps an unresolved
		// identifier un-stageable, replacing the action-less `checking` team card that used to do it by
		// blanking the whole dropdown. Staging a raw invite while the directory search is in flight is
		// the real harm the card was guarding: the address may belong to a teammate the searched slice
		// has not returned yet, and staging it here classifies them as a non-member with the wrong
		// role. Suppressing the ROW rather than guarding the two add paths is deliberate — the click
		// handler reads `data-invite`, which only renders when this computed is non-null, and onEnter
		// falls through to this same value, so one rule covers both entrances instead of two guards
		// that can drift. Directory hits are unaffected and remain clickable; the in-flight state is
		// labelled by `.rs-srchpending`, and this clears in one round trip.
		if (props.searching) return null;
		if (email ? usedSet.value.has(emailComparisonKey(value)) : usedSet.value.has(value.toLowerCase())) return null;
		// A national-format phone that resolves to an already-shared E.164 must NOT offer an
		// Invite row (that's the duplicate-share bug) — check the normalized key too.
		if (phone && !email) {
			const key = phoneKey(value);
			if (key && usedSet.value.has(key)) return null;
		}
		if (directoryHits.value.some((hit) => hit.emails.some((address) => emailComparisonKey(address) === emailComparisonKey(value))))
			return null;
		// Round-5 finding 1: the email comparison above is always false for a phone-shaped value
		// (emailComparisonKey normalizes an ADDRESS), so before the phone-search work this suppression
		// was safe only BY OMISSION — a phone query could never resolve to a directory contact. Now it
		// can, and without this the dropdown rendered the matched contact AND a redundant raw
		// "Invite <number>" row that could be staged: the very harm the original Bugbot HIGH described.
		//
		// PREDICATE CHOICE: matched with phoneSearchCandidates (the SEARCH-side normalization), not a
		// third notion. That is not a staging-vs-search inconsistency, because we are already past the
		// strict `isPhone(value)` gate above — isPhoneSearchQuery is a superset of isPhoneQuery, so for
		// any value that can reach this line the two agree and yield identical candidates. Aligning
		// with the FILTER is what matters: suppress exactly when a hit was rendered FOR this number,
		// so a number nobody has still gets its invite row.
		//
		// Round 6 (thread 3824178697): this line used to pass the ORG country while the filter used
		// the helper default, so the two candidate sets differed for a non-US org and a hit the filter
		// had already rendered could fail this check — the raw invite row survived beside the matched
		// contact. phoneSearchCandidates no longer ACCEPTS a country, so both sites are structurally
		// on one basis and cannot drift again.
		if (phone && !email) {
			const typedPhoneCandidates = phoneSearchCandidates(value);
			// Across ALL of the hit's numbers, for the same reason as the email suppression above
			// (Bugbot thread 3861233836): the filter can now render a row matched on its SECONDARY
			// number, and comparing only the primary here would leave the redundant raw invite row.
			if (directoryHits.value.some((hit) => hit.phones.some((number) => phoneMatchesCandidates(number, typedPhoneCandidates))))
				return null;
		}
		return { value, phone: phone && !email };
	});

	// LSP-17773: a typed email/phone that ALREADY has access (an existing share or a
	// pending invite) — any domain, not just same-company — should say so and point to the
	// list, instead of the misleading "No people match". A phone is matched on its E.164 form
	// so a typed national number recognizes an existing share stored with its country code.
	const alreadyAdded = computed(() => {
		const value = query.value.trim();
		if (!value || (!isEmail(value) && !isPhone(value))) return null;
		const key = isEmail(value) ? emailComparisonKey(value) : value.toLowerCase();
		if (usedSet.value.has(key)) return value;
		if (isPhone(value)) {
			const normalized = phoneKey(value);
			if (normalized && usedSet.value.has(normalized)) return value;
		}
		return null;
	});

	// PR #3838 review H1: true while the typed query is too short for the parent to have searched
	// the server at all — the empty state must ask for more input, not assert a non-match.
	const queryBelowSearchMinimum = computed(() => {
		// Break-it round 2: `??` only defends against null/undefined. A non-finite value (NaN from a
		// bad binding) would make EVERY comparison false and silently disable this branch, putting
		// the flat "No people match" back on a 1-char query — the exact H1 regression. Range-check it.
		const minimum = Number.isFinite(props.minSearchLength) ? (props.minSearchLength as number) : MIN_DIRECTORY_SEARCH_LENGTH;
		return query.value.trim().length < minimum;
	});

	// The dropdown always shows while there is a query: even with no team card, directory hits, or
	// invite row it renders the "already has access" notice or the "No people match" empty state.
	const showDropdown = computed(() => open.value && !!query.value.trim() && !emailError.value && !isSearchTooLong(query.value));

	function updateSuggestionsViewportCap() {
		if (!showDropdown.value || !suggestionsRef.value) return;
		const menuTop = Math.max(0, suggestionsRef.value.getBoundingClientRect().top);
		const visualViewport = window.visualViewport;
		const viewportBottom = (visualViewport?.offsetTop ?? 0) + (visualViewport?.height ?? window.innerHeight);
		// LSP-19139: the parent Share dialog (ShareActionDialog's `.rs-dlg.share`) intentionally sets
		// `overflow: visible` so this dropdown can escape the dialog's own clipping (LSP-18988) instead
		// of being cut off mid-list. That trade only holds while the dropdown still ends at the
		// DIALOG's own bottom edge — capping purely to the browser viewport let it hang past the
		// dialog's white card into the dimmed backdrop below it, which reads as the autocomplete
		// "overlapping and obscuring" the rest of the page instead of floating cleanly above it.
		// An isolated mount (e.g. in tests, or a future non-dialog usage) has no `.rs-dlg` ancestor and
		// keeps the previous viewport-only behaviour.
		const dialogElement = suggestionsRef.value.closest(".rs-dlg") as HTMLElement | null;
		// LSP-19139 follow-up: clamp to the FOOTER's own top edge, not the dialog's outer bottom edge,
		// when a footer exists. The dialog's action footer (Cancel/Manage access/Share) sits flush with
		// the dialog's bottom, so capping to the outer edge let a tall enough list render its bottom
		// rows UNDER the footer buttons — visible but unclickable-looking and confusing. This never
		// changes the DIALOG's own size (see ShareActionDialog.vue's fixed `.rs-dlg.share` min-height,
		// which is what actually gives this room, applied unconditionally so the card never visibly
		// resizes as search results change) — it only tightens where THIS dropdown is allowed to end.
		const footerElement = dialogElement?.querySelector(":scope > .rs-foot") as HTMLElement | null;
		const dialogOrFooterBottom = footerElement
			? footerElement.getBoundingClientRect().top
			: dialogElement
				? dialogElement.getBoundingClientRect().bottom
				: Number.POSITIVE_INFINITY;
		const lowerBound = Math.min(viewportBottom, dialogOrFooterBottom);
		const spaceBelow = Math.max(0, lowerBound - menuTop);
		suggestionsRef.value.style.setProperty("--rs-sugg-space-below", `${spaceBelow}px`);

		// guards LSP-19139 (AI review) — `.rs-sugg`'s own CSS used to floor its shrink-to-fit height at
		// a FIXED 6rem (LSP-19141, so a tiny/negative space-below never read as "the suggestions
		// disappeared"). That fixed floor can itself exceed `spaceBelow` on a short enough viewport —
		// real landscape-phone heights (~375px) land here — which would push the dropdown's own bottom
		// edge PAST the footer clamp above, exactly the overlap this whole follow-up exists to prevent.
		// Rather than hardcode 6rem in CSS, compute a floor that adapts to what's actually available:
		// still 6rem whenever there's room for it (unchanged from before), shrinking to fit when there
		// isn't, and never below a small absolute minimum (2rem — still visibly a dropdown, not empty)
		// so a truly pathological viewport gets a small guaranteed overlap instead of a large one.
		const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
		const PREFERRED_FLOOR_PX = 6 * remPx;
		const ABSOLUTE_MIN_FLOOR_PX = 2 * remPx;
		const availableForFloor = spaceBelow - remPx; // mirrors the CSS formula's own `- 1rem`
		const safeFloorPx = Math.min(PREFERRED_FLOOR_PX, Math.max(ABSOLUTE_MIN_FLOOR_PX, availableForFloor));
		suggestionsRef.value.style.setProperty("--rs-sugg-safe-floor", `${safeFloorPx}px`);
	}

	// LSP-19141: the cap above used to only recalculate on the closed -> open transition. A second
	// search performed WITHOUT the dropdown ever fully closing (the query never drops back to empty —
	// e.g. selecting all the typed text and typing over it in one edit) left `--rs-sugg-space-below`
	// pinned to whatever the FIRST search computed. Watching `directoryHits` too means a genuinely new
	// result set always gets a fresh measurement instead of rendering into a stale — and potentially
	// much smaller — cap that reads as "the suggestions disappeared".
	watch([showDropdown, directoryHits], async ([visible]) => {
		if (!visible) return;
		await nextTick();
		updateSuggestionsViewportCap();
	});

	onMounted(() => {
		window.addEventListener("resize", updateSuggestionsViewportCap);
		window.visualViewport?.addEventListener("resize", updateSuggestionsViewportCap);
		window.visualViewport?.addEventListener("scroll", updateSuggestionsViewportCap);
	});

	onBeforeUnmount(() => {
		window.removeEventListener("resize", updateSuggestionsViewportCap);
		window.visualViewport?.removeEventListener("resize", updateSuggestionsViewportCap);
		window.visualViewport?.removeEventListener("scroll", updateSuggestionsViewportCap);
	});

	function toTitleCase(value: string) {
		return value.replace(/[._-]/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
	}

	function onSuggMouseDown(event: MouseEvent) {
		const target = event.target as HTMLElement;
		const teamButton = target.closest("[data-tmteam]") as HTMLElement | null;
		if (teamButton) {
			// LSP-17773 (review): hand the typed email to the parent, which routes to the real
			// team-invite flow (/users/new prefilled — seat checks + the standard invite email).
			// Previously this just closed the panel, silently abandoning the action.
			// The email is read from the RENDERED attribute — what the user actually saw. The
			// computed fallback and the logged else-branch are DEFENSIVE (review: with the current
			// template they are unreachable, since the button only renders with a non-empty
			// :data-tmteam) — kept so a future template change can degrade loudly, not silently.
			const email = teamButton.getAttribute("data-tmteam") || teamCard.value?.email || "";
			// No reset() here (Cursor): the parent may REFUSE the hand-off (staged-queue guard) and
			// keep the dialog open — the typed address and its card must survive that refusal so
			// the admin doesn't retype it. On a successful hand-off the whole dialog closes and
			// navigates, and the next open's init() resets everything anyway.
			if (email) {
				emit("add-to-team", email);
			} else {
				logError(
					"ShareInviteField: 'Add to Team Instead' clicked but no email could be resolved — nothing emitted.",
					new Error("unresolvable add-to-team click")
				);
			}
			return;
		}
		const tmShare = target.closest("[data-tmshare]") as HTMLElement | null;
		if (tmShare) {
			const email = tmShare.getAttribute("data-tmshare") || "";
			const member = tmShare.getAttribute("data-member") === "1";
			const name = toTitleCase(email.split("@")[0]);
			stage(member ? { name, email, teammate: true, role: "editor" } : { name, email });
			return;
		}
		const suggRow = target.closest("[data-sg]") as HTMLElement | null;
		if (suggRow) {
			// Resolve the RENDERED hit (see stageDirectoryHit): directoryHits is exactly what the
			// template loops over, so a clickable row is always in it, and a miss here is the same
			// stale-row condition the old props.directory lookup detected.
			const hit = directoryHits.value.find((entry) => entry.id === suggRow.getAttribute("data-sg"));
			if (hit) {
				// develop (LSP-18863): already-shared rows render as informational and are intentionally
				// non-actionable. Gated on the resolved hit rather than the rendered
				// `data-already-access` attribute so the flag has ONE source of truth — the attribute is
				// the same computed value, and re-reading it from the DOM would be a second entrance to
				// the same decision.
				if (hit.alreadyHasAccess) return;
				stageDirectoryHit(hit, "click");
			} else {
				// AI-review finding 4 (231f07b71c): the directory is now REPLACED asynchronously by
				// each debounced search result, so a row the user can see can disappear between Vue's
				// render and their mousedown (superseded result, or the row just got staged/filtered by
				// usedSet). That made a visible "Add" silently do nothing.
				logError(
					"ShareInviteField: clicked suggestion row is no longer in the directory — nothing staged.",
					new Error("stale directory row click"),
					{ requestedId: suggRow.getAttribute("data-sg") }
				);
			}
			return;
		}
		const inviteEl = target.closest("[data-invite]") as HTMLElement | null;
		if (inviteEl) {
			const value = inviteEl.getAttribute("data-invite") || "";
			const phone = inviteEl.getAttribute("data-phone") === "1";
			if (phone) {
				stage({ name: value, phone: value, phoneOnly: true });
			} else {
				stage({ name: toTitleCase(value.split("@")[0]), email: value });
			}
		}
	}

	function onEnter() {
		if (isSearchTooLong(query.value)) return;
		if (!validateEmailQuery("enter")) return;
		if (teamCard.value) {
			// Round 7: the `checking` half of this guard is GONE, not relocated — teamCard no longer
			// produces that variant, so testing for it here could not fail and would only imply a state
			// that cannot occur. The protection it provided (never stage an email whose membership is
			// still resolving) now lives in `inviteRow`, which returns null while `searching` and is the
			// single value both this fallthrough and the click handler read.
			//
			// `added` still consumes the keypress: the card is informational and staging would duplicate
			// an existing share.
			if (teamCard.value.variant === "added") {
				return;
			}
			const member = teamCard.value.variant === "member";
			const name = toTitleCase(teamCard.value.email.split("@")[0]);
			stage(member ? { name, email: teamCard.value.email, teammate: true, role: "editor" } : { name, email: teamCard.value.email });
			return;
		}
		if (directoryHits.value.length) {
			// Bugbot HIGH thread 3861665233: this branch used to stage first.email / first.phone — the
			// PRIMARIES — while the click path staged the matched identifier, so typing a secondary
			// number or address and pressing Enter invited the wrong person. Both paths now go through
			// stageDirectoryHit, which is the only thing that decides. Round-5's github-actions
			// finding 2 was the same "fixed one of two identical paths" mistake on the logging, and
			// that log now lives in the shared producer for the same reason.
			//
			// develop (LSP-18863): pick the first STAGEABLE hit, not directoryHits[0] — index 0 can now be
			// an informational already-shared row, and blindly staging it would duplicate a share the
			// disabled row exists to warn about. When every match is informational, fall through to the
			// visible Invite row instead of swallowing Enter.
			const first = directoryHits.value.find((hit) => !hit.alreadyHasAccess);
			if (first) {
				stageDirectoryHit(first, "enter");
				return;
			}
		}
		if (inviteRow.value) {
			const { value, phone } = inviteRow.value;
			if (phone) {
				stage({ name: value, phone: value, phoneOnly: true });
			} else {
				stage({ name: toTitleCase(value.split("@")[0]), email: value });
			}
		}
	}

	/**
	 * The ONE place a directory hit becomes a staged entry.
	 *
	 * Bugbot HIGH thread 3861665233. Thread 3861458841 fixed the CLICK path to stage the matched
	 * identifier and left Enter staging `first.email`/`first.phone` — the same show/send mismatch,
	 * surviving on the other add path, because the two paths each decided the identifier themselves.
	 * That is the identical failure mode as the filter/subtitle/staging split one level down, so it
	 * gets the identical remedy: one function, two callers, no second copy of the decision.
	 *
	 * Both callers pass the RENDERED hit (`directoryHits` is what the template loops over, and Enter
	 * takes the first stageable entry from it), so neither re-derives the match — re-derivation is how
	 * these drifted apart in the first place.
	 *
	 * No dedup lives here on purpose, but WHERE it lives changed in the develop merge. It used to be
	 * the `directoryHits` FILTER, which dropped already-shared rows entirely; develop's LSP-18863
	 * keeps them and renders them disabled, so `directoryHits` now MARKS them via
	 * `alreadyHasAccess` (computed from the same matched identifiers this function stages — see
	 * `stagedIdentifiersHaveAccess`). Both callers must therefore refuse a marked hit before calling
	 * in: the click path returns early on `hit.alreadyHasAccess`, and the Enter path picks the first
	 * UNMARKED hit rather than index 0. Guarding only one of them is the partial-latch shape that
	 * thread 3861665233 was filed for, so if a third add path ever appears it guards too.
	 */
	function stageDirectoryHit(hit: DirectoryHit, via: "click" | "enter") {
		// Bugbot thread 3865503645: this function READS the decision, it does not make it. It used to
		// re-derive `hit.matchedEmail || hit.email` and `hit.matchedPhone || hit.phone` here, which is
		// how a primary-address fallback intended for the SUBTITLE ended up choosing the invite
		// channel. Deliberately no `||` fallback below: if `stageTarget` is null there is genuinely
		// nothing to send to, and re-adding one here reintroduces the same bug one layer down.
		const target = hit.stageTarget;
		// The teammate test stays on the PRIMARY address, and deliberately does NOT consult `target`:
		// it asks "is this person on my team", an identity question that `teamEmails` is keyed on, and
		// staging a non-team address with role: editor would be wrong. This is also why a teammate
		// found BY PHONE is still added as an editor by their account email rather than texted — for a
		// teammate the account is the thing being granted access, not a contact channel. Team-member
		// rows carry single-element match sets anyway (memberItems builds `emails: [email]` from the
		// scalar Users columns buildUserSearchWhere matches), so matched and primary coincide for them.
		//
		// ls-heroku-ci FULL review at 578ef71f2d (low): can `alreadyHasAccess` and the staged
		// identifier diverge here — a teammate matched by PHONE gets `stageTarget = { phone }`, so the
		// row's disabled state was decided about the number while this branch sends to the email?
		// No, and not by luck: `stagedIdentifiersHaveAccess` delegates FIRST to
		// `itemAlreadyHasAccess`, whose primary-email check tests `hit.email` — the exact value this
		// branch stages. So a teammate whose account email already has access is marked informational
		// regardless of which identifier the query matched, and this branch is unreachable for them.
		// The guarantee is structural, but it is a coupling worth naming: if that primary-email check
		// is ever narrowed, this branch needs its own.
		const member = !!hit.email && teamSet.value.has(emailComparisonKey(hit.email));
		if (member) {
			stage({ name: hit.name, email: hit.email, teammate: true, role: "editor" });
		} else if (target?.field === "email") {
			// `name` deliberately does NOT follow the match: it is a stable identity label persisted
			// with the share, so making it query-dependent would file one contact under different
			// names in different searches.
			//
			// LSP-18909: `contact: true` is set here for the same reason the phone branch below sets
			// it — everyone reaching this function came out of the directory search, so they are a
			// contact by construction. Without it an emailed contact staged with NO tag while a
			// phone-only contact from the same result set showed "contact", which is the "tags are
			// applied selectively" report. Setting it in this single producer covers both the click
			// and Enter paths; before develop's refactor it had to be set at each call site.
			stage({ name: hit.name, email: target.value, contact: true });
		} else if (target?.field === "phone") {
			stage({ name: hit.name, phone: target.value, phoneOnly: true, contact: true, customerId: hit.id });
		} else {
			// AI-review finding 4 / round-5 finding 2: a row with neither an email nor a phone has
			// nothing to stage. Pre-existing shape, but silent — log it rather than swallowing the
			// interaction, and log it for BOTH paths (previously only Enter carried `via`).
			logError(
				"ShareInviteField: suggestion row has neither an email nor a phone — nothing staged.",
				new Error("unstageable directory row"),
				{ itemId: hit.id, via }
			);
		}
	}

	function stage(entry: Record<string, unknown>) {
		emit("stage", entry);
		reset();
	}

	function reset() {
		query.value = "";
		// LSP-19008: this used to re-derive the panel state from `document.activeElement`. reset() is
		// only ever reached from stage(), which runs synchronously inside the panel's `mousedown`
		// handler — focus is mid-flight at that point (the panel holds it on the input via
		// `@mousedown.prevent`), so that read is not reliable and could latch `open` to false. Since
		// `showDropdown` is `open && !!query.trim()`, every subsequent keystroke was then filtered out
		// and the field looked dead until a real focus event re-opened it: the caret was still visibly
		// in the input, so nothing told the user to click away and back.
		//
		// Staging someone is the middle of the "add people" flow, not the end of it, so assert the
		// intent instead of sampling it: keep the caret in the field and leave the panel armed. The
		// cleared query keeps it hidden until the user actually types again, and onBlur still closes it
		// when focus genuinely leaves.
		inputRef.value?.focus();
		open.value = true;
	}

	function onBlur() {
		window.setTimeout(() => {
			if (document.activeElement !== inputRef.value) open.value = false;
		}, 150);
	}
</script>

<style scoped>
	/* Ported verbatim from design-ref/lp-real.css (.rs-field / .rs-sugg / .tm-card). */
	.rs-suggwrap {
		--blue: #2563eb;
		--ink: #111827;
		--ink2: #374151;
		--ink3: #6b7280;
		--line: #d5dae2;
		--line2: #eceff3;
		display: flex;
		flex: 0 0 auto;
		flex-direction: column;
		min-height: 0;
		position: relative;
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif;
	}
	.rs-field {
		position: relative;
		border: 1px solid #94a3b8;
		border-radius: 10px;
		padding: 14px 52px 13px 16px;
		margin-bottom: 0;
	}
	.rs-field .inner {
		display: flex;
		align-items: center;
		gap: 12px;
		min-height: 26px;
	}
	.rs-field .inner .pico {
		color: var(--ink2);
		display: flex;
		flex-shrink: 0;
	}
	.rs-field input {
		border: 0;
		outline: 0;
		font: inherit;
		font-size: 16px;
		color: var(--ink);
		flex: 1;
		background: transparent;
		min-width: 0;
	}
	.rs-field input::placeholder {
		color: var(--ink3);
	}
	.rs-field .trail {
		position: absolute;
		right: 14px;
		top: 50%;
		transform: translateY(-50%);
		display: flex;
		gap: 14px;
		align-items: center;
		color: var(--blue);
	}
	.rs-plusbtn:hover {
		color: #1d4fd7;
	}
	.rs-caption {
		font-size: 12px;
		color: var(--ink3);
		line-height: 1.5;
		margin: 6px 2px 18px;
	}
	.rs-caption b {
		color: var(--ink2);
		font-weight: 700;
	}
	.rs-caption-under-menu {
		visibility: hidden;
	}
	.rs-sugg .rs-caption {
		margin: 6px 10px 4px;
	}
	.rs-sugg {
		position: absolute;
		top: 100%;
		left: 0;
		right: 0;
		/* LSP-19141: floor the shrink-to-fit height (roughly two rows at most) instead of 0px. When the
		   computed space below the field is very small (or briefly negative before a resize/scroll
		   listener catches up), `max(0px, …)` collapsed this to a genuinely zero-height box — still
		   `display: block`, but with nothing visible, which reads exactly like "the suggestions
		   disappeared" even though they rendered. A small, scrollable list is a better failure mode than
		   an invisible one.
		   LSP-19139 (AI review): the floor used to be the fixed `6rem` literal this comment originally
		   described. A fixed floor can itself exceed the real available room on a short viewport,
		   overshooting the footer clamp below — `--rs-sugg-safe-floor` (updateSuggestionsViewportCap)
		   is that same 6rem whenever there's room for it, but adapts down (never below 2rem) when there
		   isn't, so this floor can no longer force the dropdown past where it's supposed to stop. */
		max-height: min(18rem, max(var(--rs-sugg-safe-floor, 6rem), calc(var(--rs-sugg-space-below, 50dvh) - 1rem)));
		overflow-y: auto;
		overscroll-behavior: contain;
		z-index: 40;
		background: #fff;
		border: 1px solid var(--line2);
		border-radius: 12px;
		box-shadow: 0 16px 40px -12px rgba(15, 23, 42, 0.3);
		padding: 6px;
		display: none;
	}
	.rs-sugg.open {
		display: block;
	}
	.rs-srow {
		display: flex;
		align-items: center;
		gap: 12px;
		padding: 10px;
		border-radius: 9px;
		cursor: pointer;
	}
	.rs-srow:hover {
		background: #f3f4f6;
	}
	.rs-srow .sm {
		flex: 1;
		min-width: 0;
	}
	.rs-srow .sn {
		font-size: 14.5px;
		font-weight: 600;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.rs-srow .se {
		font-size: 12.5px;
		color: var(--ink3);
		margin-top: 1px;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.rs-srow .sadd {
		font-size: 13px;
		font-weight: 700;
		color: var(--blue);
		flex-shrink: 0;
	}
	.rs-srow.invite .plus {
		width: 36px;
		height: 36px;
		border-radius: 999px;
		background: #eef4ff;
		color: var(--blue);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.rs-srow.invite .sn {
		color: var(--blue);
	}
	.r-cava {
		width: 34px;
		height: 34px;
		border-radius: 999px;
		background: #bfe0f5;
		color: #0f3d5c;
		font-weight: 700;
		font-size: 12px;
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.rs-empty {
		padding: 14px;
		font-size: 13px;
		color: var(--ink3);
		text-align: center;
	}
	/* AI-review finding 2: non-blocking "results may be incomplete" caveat rendered above the hits.
	   .rs-srchpending is its in-flight sibling (round-5 finding 2). */
	.rs-srchpending,
	.rs-srchwarn {
		padding: 10px 14px;
		font-size: 12px;
		color: var(--ink3);
		text-align: center;
	}
	.tm-card {
		display: flex;
		gap: 13px;
		padding: 14px 12px;
		align-items: flex-start;
	}
	.tm-ico {
		width: 40px;
		height: 40px;
		border-radius: 999px;
		background: #eef4ff;
		color: var(--blue);
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
	}
	.tm-body {
		flex: 1;
		display: flex;
		flex-direction: column;
		gap: 5px;
		font-size: 13px;
		color: var(--ink3);
		line-height: 1.5;
		min-width: 0;
		/* Long invited emails (e.g. robert.teasley+projectshare@liveswitch.com) have no spaces,
		   so without this they overflow/clip the fixed-width card on narrow viewports. Break the
		   address only when it can't otherwise fit. */
		overflow-wrap: anywhere;
	}
	.tm-body strong {
		font-size: 14.5px;
		color: var(--ink);
		font-weight: 700;
	}
	.tm-actions {
		display: flex;
		gap: 9px;
		margin-top: 7px;
		flex-wrap: wrap;
	}
	.tm-primary {
		height: 36px;
		padding: 0 16px;
		background: var(--blue);
		color: #fff;
		border: 0;
		border-radius: 9px;
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
	}
	.tm-primary:hover {
		background: #1d4fd7;
	}
	.tm-secondary {
		height: 36px;
		padding: 0 16px;
		background: #fff;
		color: var(--ink2);
		border: 1px solid var(--line);
		border-radius: 9px;
		font-size: 13px;
		font-weight: 700;
		cursor: pointer;
	}
	.tm-secondary:hover {
		background: #f6f7f9;
	}
</style>
